// POS voids + removed/cancelled items for management (data from the existing Day Report cancellation records).
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { PosVoidDetailDialog } from './PosVoidDetailDialog';
import api from '../../lib/api';

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => today().slice(0, 8) + '01';
const ts = (s) => (s ? new Date(s).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '—');

export function PosVoidHistory() {
  const [f, setF] = useState({ start_date: monthStart(), end_date: today(), branch_id: 'all', cashier: '', reason: '' });
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState({ bills: [], items: [] });
  const [openId, setOpenId] = useState(null);
  useEffect(() => { api.get('/branches').then(({ data }) => setBranches(data || [])).catch(() => {}); }, []);
  const load = useCallback(() => {
    const params = { ...f, branch_id: f.branch_id === 'all' ? undefined : f.branch_id };
    api.get('/cashier/void-history', { params }).then(({ data }) => setData(data)).catch(() => setData({ bills: [], items: [] }));
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const bname = (id) => branches.find((b) => b.id === id)?.name || '—';
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const rows = [
    ...data.bills.map((b) => ({ ...b, kind: 'Bill void', who: b.voided_by })),
    ...data.items.map((i) => ({ ...i, kind: i.stage === 'before_kitchen' ? 'Item removed' : 'Item cancelled (kitchen)', who: i.cancelled_by })),
  ].sort((a, b) => String(b.at).localeCompare(String(a.at)));
  return (
    <Card data-testid="pos-void-history">
      <CardHeader className="space-y-3">
        <CardTitle className="text-base">POS voids &amp; item cancellations ({rows.length}) · bills {data.bills_total || 0} · items {data.items_total || 0}</CardTitle>
        <div className="flex flex-wrap gap-2">
          <Input type="date" value={f.start_date} onChange={set('start_date')} className="h-9 w-40" data-testid="pos-void-start" />
          <Input type="date" value={f.end_date} onChange={set('end_date')} className="h-9 w-40" data-testid="pos-void-end" />
          <Select value={f.branch_id} onValueChange={set('branch_id')}>
            <SelectTrigger className="h-9 w-44" data-testid="pos-void-branch"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Cashier" value={f.cashier} onChange={set('cashier')} className="h-9 w-40" data-testid="pos-void-cashier" />
          <Input placeholder="Reason" value={f.reason} onChange={set('reason')} className="h-9 w-40" data-testid="pos-void-reason" />
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {rows.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="pos-void-empty">No voids in this period.</p> : (
          <table className="w-full text-sm" data-testid="pos-void-table">
            <thead><tr className="text-left text-xs text-muted-foreground border-b">
              <th className="py-2 pr-3">When</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Bill</th><th className="py-2 pr-3">Branch</th>
              <th className="py-2 pr-3">Cashier</th><th className="py-2 pr-3">By / approved</th><th className="py-2 pr-3 text-right">Amount</th><th className="py-2">Reason</th>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={`${r.order_id}-${i}`} className="border-b border-border/60 cursor-pointer hover:bg-muted/40" onClick={() => setOpenId(r.order_id)} data-testid={`pos-void-row-${r.order_id}`}>
                <td className="py-2 pr-3 text-xs whitespace-nowrap">{ts(r.at)}</td>
                <td className="py-2 pr-3"><Badge variant="outline">{r.kind}</Badge></td>
                <td className="py-2 pr-3 text-xs">#{r.order_number}{r.item ? ` · ${r.quantity} × ${r.item}` : ''}</td>
                <td className="py-2 pr-3 text-xs">{bname(r.branch_id)}</td>
                <td className="py-2 pr-3 text-xs">{r.cashier || '—'}</td>
                <td className="py-2 pr-3 text-xs">{r.who || '—'} / {r.approved_by || '—'}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{r.amount ?? '—'}</td>
                <td className="py-2 text-xs">{r.reason || '—'}</td>
              </tr>))}
            </tbody>
          </table>
        )}
      </CardContent>
      <PosVoidDetailDialog orderId={openId} onOpenChange={(o) => !o && setOpenId(null)} />
    </Card>
  );
}
